/**
 * Knowledge Graph Design Wizard
 *
 * Main orchestration component for the three-phase workflow:
 * 1. Schema Exploration - Browse and select tables
 * 2. Relationship Discovery - Discover and approve relationships
 * 3. Schema Preview - Visualize and analyze the final schema
 */

import React, { useState, useCallback } from 'react';
import {
  Database,
  GitBranch,
  Eye,
  Check,
  ChevronRight,
  ChevronLeft,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import Button from '../ui/Button';
import { Badge } from '../ui/Badge';
import SchemaExplorationPanel from './SchemaExplorationPanel';
import RelationshipDiscoveryPanel from './RelationshipDiscoveryPanel';
import SchemaPreviewPanel from './SchemaPreviewPanel';
import SuggestedOntologyView from './SuggestedOntologyView';
import ErrorBoundary from '../ui/ErrorBoundary';
import { knowledgeGraphDesignApi } from '../../lib/api/knowledgeGraphDesignApi';
import type { SchemaPreview, CompleteSchemaResponse } from '../../types/knowledgeGraphDesign';

/**
 * Suggested Ontology/Schema mode (Phase 1, T1.8) — ADDITIVE & FLAG-GATED.
 *
 * Enabled only when VITE_ENABLE_SUGGESTED_SCHEMA is truthy (mirrors the backend
 * `enable_suggested_schema` flag) OR the `enableSuggestedSchema` prop is set. When
 * OFF, this wizard behaves byte-identically to before — the existing table-based
 * and KG-First modes/phases are untouched.
 */
const SUGGESTED_SCHEMA_FLAG =
  String(import.meta.env.VITE_ENABLE_SUGGESTED_SCHEMA ?? '').toLowerCase() === 'true';

interface KnowledgeGraphDesignWizardProps {
  caseId: string;
  systemIds: string[];
  systemNames: string[];
  currentUser: string;
  questionIds?: string[];
  /**
   * Called after the schema is saved. Receives the API result AND the schema
   * preview that was saved, so the host wizard can seed its own store and let
   * the user continue to the next step instead of being navigated away.
   */
  onComplete?: (result?: CompleteSchemaResponse, savedPreview?: SchemaPreview) => void;
  onCancel?: () => void;
  /**
   * Navigate back to the previous step of the HOST wizard (e.g. Analysis → Data
   * Mapping). Optional & additive: when provided, a "Back" control is shown in the
   * Suggested Ontology pre-step header. When omitted, nothing changes.
   */
  onBack?: () => void;
  /**
   * Use KG-First schema generation (concept-first approach).
   * When true, Phase 3 uses generateKGFirstSchema instead of generateSchemaPreview.
   * This maps tables to KG Area taxonomy concepts rather than 1:1 table-to-node conversion.
   * Default: true for Advanced Design workflow.
   */
  useKGFirst?: boolean;
  /**
   * Suggested Ontology/Schema mode (Phase 1, T1.8) — ADDITIVE & FLAG-GATED.
   * Defaults to the VITE_ENABLE_SUGGESTED_SCHEMA build flag. When enabled, a new
   * "Suggest Ontology" step is shown first; accepting its schema hands off into the
   * existing wizard phases. Pass `false` to force the legacy behavior.
   */
  enableSuggestedSchema?: boolean;
  /** Optional industry hint for the suggested-ontology picker. */
  industry?: string;
}

type PhaseId = 1 | 2 | 3;

interface Phase {
  id: PhaseId;
  title: string;
  description: string;
  icon: React.ElementType;
}

// Phases for traditional table-based workflow
const tableBasedPhases: Phase[] = [
  {
    id: 1,
    title: 'Select Tables',
    description: 'Browse and select tables from the enterprise system',
    icon: Database,
  },
  {
    id: 2,
    title: 'Discover Relationships',
    description: 'Find and approve connections between tables',
    icon: GitBranch,
  },
  {
    id: 3,
    title: 'Preview Schema',
    description: 'Review the final schema with visualizations',
    icon: Eye,
  },
];

// Phases for KG-First concept-based workflow (skips relationship discovery)
const kgFirstPhases: Phase[] = [
  {
    id: 1,
    title: 'Select Concepts',
    description: 'AI recommends conceptual entities from KG Areas',
    icon: Database,
  },
  {
    id: 3, // Skip phase 2 - concepts already include semantic relationships
    title: 'Generate Schema',
    description: 'Generate Knowledge Graph schema from concepts',
    icon: Eye,
  },
];

export const KnowledgeGraphDesignWizard: React.FC<KnowledgeGraphDesignWizardProps> = ({
  caseId,
  systemIds,
  systemNames,
  currentUser,
  questionIds,
  onComplete,
  onCancel,
  onBack,
  useKGFirst = true, // Default to KG-First for Advanced Design
  enableSuggestedSchema,
  industry,
}) => {
  // Use appropriate phases based on mode
  const phases = useKGFirst ? kgFirstPhases : tableBasedPhases;

  // Suggested Ontology mode (Phase 1) — flag-gated, additive. When ON, show the
  // suggested-ontology pre-step until the user accepts (or skips into) the existing
  // wizard. When OFF the existing flow is unchanged.
  const suggestedSchemaEnabled = enableSuggestedSchema ?? SUGGESTED_SCHEMA_FLAG;
  const [showSuggestedView, setShowSuggestedView] = useState<boolean>(suggestedSchemaEnabled);

  const [currentPhase, setCurrentPhase] = useState<PhaseId>(1);
  const [completedPhases, setCompletedPhases] = useState<Set<PhaseId>>(new Set());
  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);
  const [approvedRelationshipIds, setApprovedRelationshipIds] = useState<string[]>([]);
  const [schemaPreview, setSchemaPreview] = useState<SchemaPreview | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);

  // Handle phase completion
  const handlePhaseComplete = useCallback((phase: PhaseId) => {
    setCompletedPhases((prev) => new Set(prev).add(phase));
  }, []);

  // Handle schema preview loaded (Phase 3)
  const handleSchemaPreviewLoaded = useCallback((preview: SchemaPreview) => {
    setSchemaPreview(preview);
  }, []);

  // Handle final completion - save schema and transition to Design phase
  const handleCompleteSchema = useCallback(async () => {
    if (!schemaPreview) {
      toast.error('Schema preview not available. Please wait for it to load.');
      return;
    }

    setIsCompleting(true);
    try {
      const result = await knowledgeGraphDesignApi.completeSchemaDesign(
        caseId,
        schemaPreview
      );

      if (result.success) {
        toast.success(result.message || 'Schema design completed successfully!');
        onComplete?.(result, schemaPreview);
      } else {
        toast.error('Failed to complete schema design');
      }
    } catch (error: any) {
      console.error('Error completing schema:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete schema design');
    } finally {
      setIsCompleting(false);
    }
  }, [caseId, schemaPreview, onComplete]);

  // Get next phase ID based on current phases array
  const getNextPhaseId = useCallback((): PhaseId | null => {
    const currentIndex = phases.findIndex(p => p.id === currentPhase);
    if (currentIndex < phases.length - 1) {
      return phases[currentIndex + 1].id;
    }
    return null;
  }, [phases, currentPhase]);

  // Get previous phase ID based on current phases array
  const getPreviousPhaseId = useCallback((): PhaseId | null => {
    const currentIndex = phases.findIndex(p => p.id === currentPhase);
    if (currentIndex > 0) {
      return phases[currentIndex - 1].id;
    }
    return null;
  }, [phases, currentPhase]);

  // Check if we're on the last phase
  const isLastPhase = useCallback((): boolean => {
    const currentIndex = phases.findIndex(p => p.id === currentPhase);
    return currentIndex === phases.length - 1;
  }, [phases, currentPhase]);

  // Navigate to next phase
  const goToNextPhase = useCallback(() => {
    const nextPhaseId = getNextPhaseId();
    if (nextPhaseId) {
      handlePhaseComplete(currentPhase);
      setCurrentPhase(nextPhaseId);
    } else {
      // Complete the wizard - save schema and transition to Design phase
      handleCompleteSchema();
    }
  }, [currentPhase, getNextPhaseId, handlePhaseComplete, handleCompleteSchema]);

  // Navigate to previous phase
  const goToPreviousPhase = useCallback(() => {
    const prevPhaseId = getPreviousPhaseId();
    if (prevPhaseId) {
      setCurrentPhase(prevPhaseId);
    }
  }, [getPreviousPhaseId]);

  // Jump to specific phase (only if previous phases are complete)
  const goToPhase = useCallback(
    (phase: PhaseId) => {
      // Can always go to earlier phases
      if (phase < currentPhase) {
        setCurrentPhase(phase);
        return;
      }
      // Can only go to next phase if current is complete
      if (phase === currentPhase + 1 && completedPhases.has(currentPhase)) {
        setCurrentPhase(phase);
      }
    },
    [currentPhase, completedPhases]
  );

  // Handle table selection (Phase 1)
  const handleTablesSelected = useCallback((tableIds: string[]) => {
    setSelectedTableIds(tableIds);
    if (tableIds.length > 0) {
      handlePhaseComplete(1);
    }
  }, [handlePhaseComplete]);

  // Handle relationships approved (Phase 2)
  const handleRelationshipsApproved = useCallback((relationshipIds: string[]) => {
    setApprovedRelationshipIds(relationshipIds);
    if (relationshipIds.length > 0) {
      handlePhaseComplete(2);
    }
  }, [handlePhaseComplete]);

  // Render phase content
  const renderPhaseContent = () => {
    switch (currentPhase) {
      case 1:
        return (
          <SchemaExplorationPanel
            caseId={caseId}
            systemIds={systemIds}
            systemNames={systemNames}
            questionIds={questionIds}
            onTablesSelected={handleTablesSelected}
          />
        );
      case 2:
        return (
          <RelationshipDiscoveryPanel
            caseId={caseId}
            systemIds={systemIds}
            systemNames={systemNames}
            currentUser={currentUser}
            onRelationshipsApproved={handleRelationshipsApproved}
          />
        );
      case 3:
        return (
          <SchemaPreviewPanel
            caseId={caseId}
            systemIds={systemIds}
            systemNames={systemNames}
            onSchemaLoaded={handleSchemaPreviewLoaded}
            useKGFirst={useKGFirst}
          />
        );
      default:
        return null;
    }
  };

  // Get phase status
  const getPhaseStatus = (phase: Phase) => {
    if (completedPhases.has(phase.id)) return 'completed';
    if (phase.id === currentPhase) return 'current';
    if (phase.id < currentPhase) return 'available';
    return 'locked';
  };

  // Suggested Ontology pre-step (flag-gated). Rendered ONLY when the flag is on and
  // the user hasn't yet accepted/skipped. Accepting hands the schema into the
  // existing phases; skipping drops straight into the legacy flow. When the flag is
  // OFF this branch is never reached and the wizard is byte-identical to before.
  if (suggestedSchemaEnabled && showSuggestedView) {
    return (
      <div className="flex flex-col bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Suggested Ontology / Schema</h2>
            <p className="text-sm text-neutral-500">
              Pick an industry/domain and let the agent suggest a starting ontology.
            </p>
          </div>
          <Button variant="ghost" onClick={() => setShowSuggestedView(false)}>
            Skip to manual design
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <SuggestedOntologyView
            caseId={caseId}
            currentUser={currentUser}
            industry={industry}
            onBack={onBack}
            onAccept={(_schema, _suggestion) => {
              // Acceptance is persisted server-side in SuggestedOntologyView.handleAccept
              // (status -> accepted) so it restores on revisit; the args are intentionally
              // not consumed here. Hand off into the existing wizard phases (table/concept
              // selection -> relationship discovery -> preview); existing phases unchanged.
              setShowSuggestedView(false);
              setCurrentPhase(1);
            }}
            onCancel={onCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Header with progress */}
      <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold">Knowledge Graph Design</h2>
            <p className="text-sm text-neutral-500">
              {systemNames.length === 1
                ? systemNames[0]
                : `${systemNames.length} systems: ${systemNames.join(', ')}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selectedTableIds.length > 0 && (
              <Badge variant="outline">
                {useKGFirst
                  ? `${selectedTableIds.length} concepts selected`
                  : `${selectedTableIds.length} tables selected`
                }
              </Badge>
            )}
            {!useKGFirst && approvedRelationshipIds.length > 0 && (
              <Badge variant="outline">{approvedRelationshipIds.length} relationships</Badge>
            )}
          </div>
        </div>

        {/* Phase indicators */}
        <div className="flex items-center justify-between">
          {phases.map((phase, index) => {
            const status = getPhaseStatus(phase);
            const Icon = phase.icon;

            return (
              <React.Fragment key={phase.id}>
                <button
                  onClick={() => goToPhase(phase.id)}
                  disabled={status === 'locked'}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                    status === 'current'
                      ? 'bg-primary-100 text-primary-700 ring-2 ring-primary-500'
                      : status === 'completed'
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : status === 'available'
                      ? 'bg-white text-neutral-600 hover:bg-neutral-100'
                      : 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      status === 'completed'
                        ? 'bg-green-500 text-white'
                        : status === 'current'
                        ? 'bg-primary-600 text-white'
                        : 'bg-neutral-200 text-neutral-500'
                    }`}
                  >
                    {status === 'completed' ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Icon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-sm">{phase.title}</div>
                    <div className="text-xs opacity-75">{phase.description}</div>
                  </div>
                </button>

                {index < phases.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      completedPhases.has(phase.id)
                        ? 'bg-green-500'
                        : 'bg-neutral-200'
                    }`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Main content - allow scrolling for large visualizations.
          Wrapped in an ErrorBoundary (keyed by phase so it resets on navigation)
          so a render crash in any phase shows a recoverable panel instead of a
          blank white screen. */}
      <div className="flex-1 overflow-y-auto">
        <ErrorBoundary key={currentPhase} label="this step">
          {renderPhaseContent()}
        </ErrorBoundary>
      </div>

      {/* Footer with navigation */}
      <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50">
        <div className="flex items-center justify-between">
          <div>
            {onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={goToPreviousPhase}
              disabled={!getPreviousPhaseId()}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>

            {!isLastPhase() ? (
              <Button
                variant="primary"
                onClick={goToNextPhase}
                disabled={
                  (currentPhase === 1 && selectedTableIds.length === 0) ||
                  (!useKGFirst && currentPhase === 2 && !completedPhases.has(1))
                }
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={goToNextPhase}
                disabled={isCompleting || !schemaPreview}
              >
                {isCompleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Complete
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeGraphDesignWizard;
